import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AuthContext from '../../../authentication/AuthContext';
import axios from 'axios';
import { useAlert } from 'react-alert';
import PresellDealTable from './PresellDealTable';
import PresellHeader from './PresellHeader';
import UserContext from '../../../user/UserContext';
import { handlePresellError } from '../AlertUtils';
import SmallPresellHeader from './SmallPresellHeader';
import LoadingSpinner from '../../../common/LoadingSpinner';

export default function PresellDetail(props) {
    const [loading, setLoading] = useState(true);
    const [presell, setPresell] = useState(null);
    const [orderId, setOrderId] = useState(false);
    const [disableSubmit, setDisableSubmit] = useState(true);
    const [updateTotals, setUpdateTotals] = useState(true);
    const [orderingTimeFinished, setOrderingTimeFinished] = useState(false);
    const [showShrinkHeader, setShowShrinkHeader] = useState(false);
    const [presellHeaderThumbnail, setPresellHeaderThumbnail] = useState({});
    const [filterTerm, setFilterTerm] = useState(!!props.filterTerm ? props.filterTerm : '');
    const [highlightedProducts, setHighlightedProducts] = useState([]);
    const [dealHiddenMap, setDealHiddenMap] = useState({});
    const [dealExpandedMap, setDealExpandedMap] = useState({});
    const [displayQuantityRestrictionMessage, setDisplayQuantityRestrictionMessage] = useState(false);
    const [isAllocation, setIsAllocation] = useState(true);
    const [bannerUrl, setBannerUrl] = useState();

    // Get CAS token from context
    const cas = useContext(AuthContext);
    const { userDetails } = useContext(UserContext);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    let handleScroll = () => {
        if (window.pageYOffset >= 500) {
            setShowShrinkHeader(true);
        } else {
            setShowShrinkHeader(false);
        }
    };

    const alert = useAlert();
    let params = useParams();
    let presellId = params.presellId;

    function updateQuantities(newData) {
        let productQuantities = {};
        presell.deals.forEach(d =>
            [...d.regularProducts, ...d.rewardProducts].forEach(p => (productQuantities[p.productCode] = 0))
        );

        for (const deal of presell.deals) {
            for (const product of deal.regularProducts) {
                for (const deliveryWindow of product.deliveryWindows) {
                    if (!!deliveryWindow.id && deliveryWindow.id === newData.deliveryWindowId) {
                        deliveryWindow.quantity = newData.quantity;
                        productQuantities[product.productCode] += newData.quantity;
                    }
                }
            }
            for (const product of deal.rewardProducts) {
                for (const deliveryWindow of product.deliveryWindows) {
                    if (!!deliveryWindow.id && deliveryWindow.id === newData.deliveryWindowId) {
                        deliveryWindow.quantity = newData.quantity;
                        productQuantities[product.productCode] += newData.quantity;
                    }
                }
            }
        }
        setUpdateTotals(true);
    }

    function quantitiesUpdated(state) {
        setUpdateTotals(state);
    }

    function curtailOrderingTime() {
        setOrderingTimeFinished(true);
    }

    function collapseDeals() {
        if (dealExpandedMap.length !== 0) {
            //code coverage pain!
            setDealExpandedMap([]);
        }
    }

    function updateOtherHeaderSubmit(flag) {
        setDisableSubmit(flag);
    }

    function updatePresellHeaderThumbnail(presellHeaderThumbnail) {
        setPresellHeaderThumbnail({
            name: presellHeaderThumbnail.name,
            totalCases: presellHeaderThumbnail.totalCases,
            orderDeadline: presellHeaderThumbnail.orderDeadline,
            orderTotal: presellHeaderThumbnail.orderTotal,
            savings: presellHeaderThumbnail.savings,
            isAllocation: ['ALLOCATION', 'REJECTED'].includes(presellHeaderThumbnail.status),
            orderingClosed: orderingTimeFinished
        });
    }

    const padDeliveryWindows = deliveryWindows => product => {
        deliveryWindows.forEach(date => {
            if (product.deliveryWindows.find(dw => dw.date === date) === undefined) {
                product.deliveryWindows.push({ date });
            }
        });

        product.deliveryWindows.sort((a, b) => (a.date < b.date ? -1 : 1));
    };

    const productMatchesFilter = (product, term, matchingProducts) => {
        const matches =
            '' + product.sequenceNumber === term ||
            '' + product.productCode === term ||
            (!!product.productDescription &&
                product.productDescription.toLowerCase().includes(term.toLowerCase()));

        matchingProducts[product.id] = matches;

        return matches;
    };

    useEffect(() => {
        let hiddenMap = dealHiddenMap;
        let matchingProducts = {};
        let matchingDeals = {};

        const dealMatchesFilter = (deal, term, matchingProducts, matchingDeals) => {
            const allProducts = [...deal.regularProducts, ...deal.rewardProducts];
            const matches = allProducts
                .map(p => productMatchesFilter(p, term, matchingProducts))
                .reduce((a, b) => a || b, false);

            matchingDeals[deal.id] = matches;

            return matches;
        };

        if (!!presell) {
            presell.deals.forEach(d => {
                hiddenMap = {
                    ...hiddenMap,
                    [d.id]: !!filterTerm && !dealMatchesFilter(d, filterTerm, matchingProducts, matchingDeals)
                };
            });
        }

        let expandedMap = matchingDeals;
        if (Object.values(matchingDeals).filter(v => v).length !== 1) {
            expandedMap = {};
        }

        setDealExpandedMap(expandedMap);
        setDealHiddenMap(hiddenMap);
        setHighlightedProducts(matchingProducts);
    }, [presell, filterTerm]);

    useEffect(() => {
        if (userDetails && userDetails.currentFranchise) {
            getOrderDetails();
        } else {
            console.log('Failed to fetch presells. User details not yet available.');
        }
    }, [userDetails, cas, presellId]);

    function getOrderDetails() {
        axios
            .get(window.CONFIG['ws.activoc-presell-public.presell'].replace('{presellId}', presellId), {
                headers: {
                    Authorization: cas,
                    'Content-Type': 'application/json',
                    'x-franchise-code': userDetails.currentFranchise.code
                }
            })
            .then(response => {
                let presell = response.data;

                presell.deals.forEach(deal => {
                    // Get a sorted list of delivery windows with no duplicates
                    let deliveryWindows = new Set();
                    deal.regularProducts.forEach(p =>
                        p.deliveryWindows.forEach(dw => deliveryWindows.add(dw.date))
                    );
                    deal.rewardProducts.forEach(p =>
                        p.deliveryWindows.forEach(dw => deliveryWindows.add(dw.date))
                    );
                    deliveryWindows = [...deliveryWindows].sort();

                    // Ensure all products in the deal have all delivery windows
                    deal.rewardProducts.forEach(padDeliveryWindows(deliveryWindows));
                    deal.regularProducts.forEach(padDeliveryWindows(deliveryWindows));
                });

                setDealExpandedMap(dealExpandedMap);
                setPresell(response.data);
                let statusesAllValid = response.data.deals
                    .map(deal => deal.status)
                    .reduce((accumulator, status) => accumulator && status === 'VALID', true);
                setDisableSubmit(!statusesAllValid);
                setOrderId(presell.orderId);

                const logicalOr = (a, b) => a || b;
                const hasMinRestrictions = presell.deals
                    .map(d => d.regularProducts.map(p => !!p.minQty).reduce(logicalOr, false))
                    .reduce(logicalOr, false);
                const hasMaxRestrictions = presell.deals
                    .map(d => d.regularProducts.map(p => !!p.maxQty).reduce(logicalOr, false))
                    .reduce(logicalOr, false);
                setDisplayQuantityRestrictionMessage(hasMinRestrictions || hasMaxRestrictions);
                updatePresellHeaderThumbnail({
                    name: '',
                    totalCases: 0,
                    orderDeadline: '0001-01-01',
                    orderTotal: 0.0,
                    savings: 0.0,
                    isAllocation: false,
                    disableSubmit: false,
                    orderingClosed: false,
                    status: ''
                });
                setBannerUrl(response.data.bannerUrl);
                setLoading(false);
            })
            .catch(error => {
                handlePresellError(alert, error, 'Could not fetch presell detail.');
                setLoading(false);
            });
    }

    return (
        <div>
            <div className={'page-header'}>
                {showShrinkHeader && (
                    <div className={'sticky'}>
                        <div className={'small-header'}>
                            <SmallPresellHeader
                                orderId={orderId}
                                presellHeaderThumbnail={presellHeaderThumbnail}
                                disableSubmit={disableSubmit}
                                updateTotals={updateTotals}
                                quantitiesUpdated={quantitiesUpdated}
                                updateOtherHeaderSubmit={updateOtherHeaderSubmit}
                                isAllocation={isAllocation}
                            />
                        </div>
                    </div>
                )}
                <div className={'content'}>
                    {orderId ? (
                        <PresellHeader
                            orderId={orderId}
                            disableSubmit={disableSubmit}
                            expandedDeals={dealExpandedMap}
                            setDealExpanded={setDealExpandedMap}
                            updateTotals={updateTotals}
                            quantitiesUpdated={quantitiesUpdated}
                            curtailOrderingTime={curtailOrderingTime}
                            collapseDeals={collapseDeals}
                            updateIsAllocation={setIsAllocation}
                            isAllocation={isAllocation}
                            displayQuantityRestrictionsMessage={displayQuantityRestrictionMessage}
                            refreshOrderQuantities={getOrderDetails}
                            updatePresellHeaderThumbnail={updatePresellHeaderThumbnail}
                            updateOtherHeaderSubmit={updateOtherHeaderSubmit}
                        />
                    ) : (
                        ''
                    )}
                </div>
            </div>
            <div className={'content'} data-testid={'presell-banner-deals-div'}>
                {bannerUrl && (
                    <img src={bannerUrl} alt={'presell image for ' + presell.name} className={'banner'} />
                )}
                {loading ? (
                    <LoadingSpinner />
                ) : (
                    !!presell && (
                        <PresellDealTable
                            presell={presell}
                            expandedDeals={dealExpandedMap}
                            setExpandedState={setDealExpandedMap}
                            hiddenDeals={dealHiddenMap}
                            highlightedProducts={!!filterTerm ? highlightedProducts : null}
                            updateQuantities={updateQuantities}
                            orderingTimeFinished={orderingTimeFinished}
                            setFilterTerm={setFilterTerm}
                        />
                    )
                )}
            </div>
        </div>
    );
}
